import { AuthenticationMethod, Operator, WorkflowTaskDetailType, WorkflowType } from '@vision/ui/enums';
import {
  Workflow,
  WorkflowCondition,
  WorkflowConditionsRoot,
  WorkflowConditionRow,
  WorkflowTicketEntity,
  WorkflowSettings,
  WorkflowTask,
  WorkflowTaskAction,
} from '@vision/ui/interfaces';
import { uuid } from './uuid.utils';

export function createWorkflowClone(workflow: Workflow, cloneNameSuffix: string): Workflow {
  const clonedWorkflow = structuredClone(workflow);

  delete clonedWorkflow.created_at;
  delete clonedWorkflow.status;
  delete clonedWorkflow.user_name;
  delete clonedWorkflow.workflow_revision;

  clonedWorkflow.id = uuid();
  clonedWorkflow.name = `${workflow.name} (${cloneNameSuffix})`;
  clonedWorkflow.tasks = clonedWorkflow.tasks?.map((task) => {
    const clonedTask = { ...task };
    delete clonedTask.id;
    return clonedTask;
  });

  return clonedWorkflow;
}

function taskHasMissingCredentials(task: WorkflowTask): boolean {
  const { action } = task;

  switch (action.detail_type) {
    case WorkflowTaskDetailType.FTP_DOWNLOAD:
      return !action.ftp_password;
    case WorkflowTaskDetailType.DB_DOWNLOAD:
      return !action.remote_db_account?.password;
    case WorkflowTaskDetailType.STORAGE_DOWNLOAD:
      return !action.credentials?.account_key && !action.credentials?.sas_token;
    case WorkflowTaskDetailType.STORAGE_UPLOAD:
      return !action.credentials?.access_key_id || !action.credentials?.secret_access_key;
    case WorkflowTaskDetailType.PUSH_REPORT_METRIC:
      return action.auth_method === AuthenticationMethod.BASIC && !action.auth_password;
    default:
      return false;
  }
}

export function getWorkflowTasksMissingCredentials(workflow?: Workflow): WorkflowTask[] {
  return workflow?.tasks?.filter(taskHasMissingCredentials) || [];
}

export function convertDayDiffToCustomRangeValue(dayDiff: number | string) {
  dayDiff = Number(dayDiff);

  return dayDiff < 0 ? [Math.abs(dayDiff) + '_days_ago'] : [dayDiff + '_days_after'];
}

export function convertCustomRangeValueToDayDiff(customRangeValue: string) {
  const regexAfter = /(\d+)_days_after/g;
  const regexBefore = /(\d+)_days_ago/g;

  if (regexBefore.test(customRangeValue)) {
    return Number(customRangeValue.split('_')[0]) * -1;
  } else if (regexAfter.test(customRangeValue)) {
    return Number(customRangeValue.split('_')[0]);
  }

  return Number(customRangeValue.split('_')[0]);
}

export function convertCustomRangeValueToTranslationKeyAndCount(customRangeValue: string) {
  const value = convertCustomRangeValueToDayDiff(customRangeValue);

  return value < 0
    ? { key: 'daysBeforeWithCount', count: Math.abs(value) }
    : { key: 'daysAfterWithCount', count: value };
}

function mapCreateTicketActionForApi(action: WorkflowTaskAction): WorkflowTaskAction {
  return {
    detail_type: action.detail_type,
    ...(typeof action.notify_team_users === 'boolean' && { notify_team_users: action.notify_team_users }),
    ...(action.ticket_pool_id && { ticket_pool_id: action.ticket_pool_id }),
    ...(action.ticket_owner_id && { ticket_owner_id: action.ticket_owner_id }),
    ...(action.ticket_owner_type && { ticket_owner_type: action.ticket_owner_type }),
    ...(action.ticket_follow_up_survey_id && { ticket_follow_up_survey_id: action.ticket_follow_up_survey_id }),
    ...(action.ticket_priority && { ticket_priority: action.ticket_priority }),
    ...(action.ticket_name && { ticket_name: action.ticket_name }),
    ...(action.ticket_root_cause_group_id && { ticket_root_cause_group_id: action.ticket_root_cause_group_id }),
    ...(action.ticket_description && { ticket_description: action.ticket_description }),
    ...(action.ticket_custom_fields && { ticket_custom_fields: action.ticket_custom_fields }),
  };
}

export const TICKET_EVENT_CONDITION_KEYS = {
  ownerId: 'owner_id',
  poolId: 'ticket_pool_id',
  priority: 'priority',
  projectId: 'ticket_project_id',
  status: 'ticket_status_id',
} as const;

type TicketEventCondition = Pick<WorkflowCondition<string>, 'key' | 'operator' | 'title' | 'value'>;

function createTicketEventCondition(key: string, value?: string, title?: string): TicketEventCondition | null {
  if (!value) {
    return null;
  }

  return {
    key,
    operator: Operator.EQUALS,
    title: [title || ''],
    value,
  };
}

function getTitleValue(condition: Partial<TicketEventCondition>): string {
  if (Array.isArray(condition?.title)) {
    return condition.title[0] || '';
  }

  return '';
}

function findTicketEventConditionInRow(
  conditionRow: Partial<WorkflowConditionRow>,
  key: string,
): Partial<TicketEventCondition> | null {
  for (const nestedCondition of conditionRow.and?.conditions || []) {
    if (nestedCondition.key === key) {
      return nestedCondition as Partial<TicketEventCondition>;
    }
  }

  return null;
}

function findTicketEventCondition(
  conditions: WorkflowConditionsRoot,
  key: string,
  excludedConditionRowId?: string,
): Partial<TicketEventCondition> | null {
  const rootConditions = conditions?.or?.conditions || [];

  for (const condition of rootConditions) {
    const simpleCondition = condition as Partial<TicketEventCondition>;

    if (simpleCondition.key === key) {
      return simpleCondition;
    }

    for (const conditionRow of condition.and?.conditions || []) {
      if (excludedConditionRowId && conditionRow.id === excludedConditionRowId) {
        continue;
      }

      const nestedCondition = findTicketEventConditionInRow(conditionRow, key);

      if (nestedCondition) {
        return nestedCondition;
      }
    }
  }

  return null;
}

function getTicketEventConditionValue(conditions: WorkflowConditionsRoot, key: string): string {
  const condition = findTicketEventCondition(conditions, key);

  return String(condition?.value || '');
}

function getTicketEventConditionTitle(conditions: WorkflowConditionsRoot, key: string): string {
  const condition = findTicketEventCondition(conditions, key);

  return getTitleValue(condition);
}

export function buildTicketEventConditions(settings?: WorkflowSettings): WorkflowConditionsRoot {
  const conditions = [
    createTicketEventCondition(
      TICKET_EVENT_CONDITION_KEYS.projectId,
      settings?.ticket_project_id,
      settings?.ticket_project?.name,
    ),
    createTicketEventCondition(
      TICKET_EVENT_CONDITION_KEYS.status,
      settings?.ticket_status_id,
      settings?.ticket_status?.name,
    ),
    createTicketEventCondition(TICKET_EVENT_CONDITION_KEYS.priority, settings?.priority, settings?.priority_title),
    createTicketEventCondition(
      TICKET_EVENT_CONDITION_KEYS.poolId,
      settings?.ticket_pool_id,
      settings?.ticket_pool?.name,
    ),
    createTicketEventCondition(TICKET_EVENT_CONDITION_KEYS.ownerId, settings?.owner_id, settings?.owner?.name),
  ].filter(Boolean) as TicketEventCondition[];

  return {
    or: {
      conditions: conditions.length
        ? [
            {
              and: {
                conditions: [
                  {
                    and: {
                      conditions,
                    },
                  },
                ],
              },
            },
          ]
        : [],
    },
  } as unknown as WorkflowConditionsRoot;
}

export function getTicketEventSettingsFromConditions(conditions?: WorkflowConditionsRoot): WorkflowSettings | null {
  if (!conditions) {
    return null;
  }

  const ticketProjectId = getTicketEventConditionValue(conditions, TICKET_EVENT_CONDITION_KEYS.projectId);
  const ticketStatusId = getTicketEventConditionValue(conditions, TICKET_EVENT_CONDITION_KEYS.status);
  const priority = getTicketEventConditionValue(conditions, TICKET_EVENT_CONDITION_KEYS.priority);
  const ticketPoolId = getTicketEventConditionValue(conditions, TICKET_EVENT_CONDITION_KEYS.poolId);
  const ownerId = getTicketEventConditionValue(conditions, TICKET_EVENT_CONDITION_KEYS.ownerId);
  const ticketProjectTitle = getTicketEventConditionTitle(conditions, TICKET_EVENT_CONDITION_KEYS.projectId);
  const ticketStatusTitle = getTicketEventConditionTitle(conditions, TICKET_EVENT_CONDITION_KEYS.status);
  const priorityTitle = getTicketEventConditionTitle(conditions, TICKET_EVENT_CONDITION_KEYS.priority);
  const ticketPoolTitle = getTicketEventConditionTitle(conditions, TICKET_EVENT_CONDITION_KEYS.poolId);
  const ownerTitle = getTicketEventConditionTitle(conditions, TICKET_EVENT_CONDITION_KEYS.ownerId);

  if (!ticketProjectId && !ticketStatusId && !priority && !ticketPoolId && !ownerId) {
    return null;
  }

  return {
    ...(ticketProjectId && {
      ticket_project_id: ticketProjectId,
      ticket_project: {
        id: ticketProjectId,
        name: ticketProjectTitle,
      },
    }),
    ...(ticketStatusId && {
      ticket_status_id: ticketStatusId,
      ticket_status: {
        id: ticketStatusId,
        name: ticketStatusTitle,
      },
    }),
    ...(priority && {
      priority: priority as WorkflowSettings['priority'],
      priority_title: priorityTitle,
    }),
    ...(ticketPoolId && {
      ticket_pool_id: ticketPoolId,
      ticket_pool: {
        id: ticketPoolId,
        name: ticketPoolTitle,
      },
    }),
    ...(ownerId && {
      owner_id: ownerId,
      owner: {
        id: ownerId,
        name: ownerTitle,
      },
    }),
  };
}

export function getTicketEventProjectFromConditions(
  conditions?: WorkflowConditionsRoot,
  excludedConditionRowId?: string,
): WorkflowTicketEntity | null {
  if (!conditions) {
    return null;
  }

  const condition = findTicketEventCondition(conditions, TICKET_EVENT_CONDITION_KEYS.projectId, excludedConditionRowId);
  const id = String(condition?.value || '');

  if (!id) {
    return null;
  }

  return {
    id,
    name: getTitleValue(condition),
  };
}

export function getTicketEventProjectIdFromConditions(
  conditions?: WorkflowConditionsRoot,
  excludedConditionRowId?: string,
): string {
  return getTicketEventProjectFromConditions(conditions, excludedConditionRowId)?.id || '';
}

function mapTicketEventConditionForApi(condition: WorkflowCondition): WorkflowCondition | null {
  if (condition.key === 'ticket_data') {
    return null;
  }

  const conditionWithoutScope = { ...condition };
  delete conditionWithoutScope.scope;

  return conditionWithoutScope;
}

function mapTicketEventConditionsForApi(conditions?: WorkflowConditionsRoot): WorkflowConditionsRoot {
  return {
    or: {
      conditions: (conditions?.or?.conditions || [])
        .map((conditionGroup) => ({
          ...conditionGroup,
          and: {
            ...conditionGroup.and,
            conditions: (conditionGroup.and?.conditions || [])
              .map((conditionRow) => ({
                ...conditionRow,
                and: {
                  ...conditionRow.and,
                  conditions: (conditionRow.and?.conditions || [])
                    .map(mapTicketEventConditionForApi)
                    .filter(Boolean) as WorkflowCondition[],
                },
              }))
              .filter((conditionRow) => conditionRow.and.conditions.length > 0),
          },
        }))
        .filter((conditionGroup) => conditionGroup.and.conditions.length > 0),
    },
  };
}

function mapChangeTicketStatusActionForApi(action: WorkflowTaskAction): WorkflowTaskAction {
  return {
    detail_type: action.detail_type,
    ...(action.ticket_status_id && { ticket_status_id: action.ticket_status_id }),
    ...(action.ticket_status_name && { ticket_status_name: action.ticket_status_name }),
  };
}

function mapPushTicketToQueueActionForApi(action: WorkflowTaskAction): WorkflowTaskAction {
  return {
    detail_type: action.detail_type,
    ...(action.ticket_queue_id && { ticket_queue_id: action.ticket_queue_id }),
    ...(action.ticket_queue_name && { ticket_queue_name: action.ticket_queue_name }),
  };
}

function mapWorkflowTaskActionForApi(action: WorkflowTaskAction): WorkflowTaskAction {
  switch (action.detail_type) {
    case WorkflowTaskDetailType.CREATE_TICKET:
      return mapCreateTicketActionForApi(action);
    case WorkflowTaskDetailType.CHANGE_TICKET_STATUS:
      return mapChangeTicketStatusActionForApi(action);
    case WorkflowTaskDetailType.PUSH_TICKET_TO_QUEUE:
      return mapPushTicketToQueueActionForApi(action);
    default:
      return action;
  }
}

export function serializeWorkflowForApi(workflow: Workflow): Workflow {
  const serializedWorkflow = {
    ...workflow,
    conditions:
      workflow.type === WorkflowType.TICKET_EVENT
        ? mapTicketEventConditionsForApi(workflow.conditions)
        : workflow.conditions,
    tasks: workflow.tasks?.map((task) => ({
      ...task,
      action: mapWorkflowTaskActionForApi(task.action),
    })),
  };

  if (workflow.type === WorkflowType.TICKET_EVENT) {
    delete serializedWorkflow.settings;
  }

  return serializedWorkflow;
}
